import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Drawer, Avatar, Select, DatePicker, Upload, Dropdown, message, Spin, Tooltip, Popconfirm } from 'antd';
import {
  EditOutlined,
  CloseOutlined,
  MoreOutlined,
  SettingOutlined,
  PaperClipOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckSquareOutlined,
  DownloadOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useSelector } from 'react-redux';
import { CKEditor } from 'ckeditor4-react';
import './TaskIssueDrawer.scss';
import { compactToolbar } from "utilities/config";

const CK_BASE_URL = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
const CK_EDITOR_URL = `${CK_BASE_URL}/staticfiles/ckeditor/4.19.0/full-all/ckeditor.js`;
const CK_CONTENTS_CSS = `${CK_BASE_URL}/staticfiles/ckeditor/4.19.0/full-all/document-style.css`;

// Inline CSS injected into the editor iframe so mention chips render the same
// way they do in saved comments — kept as a data: URL to avoid an extra static
// file just for this snippet.
const MENTION_CHIP_CSS = '.mention{background:#ddf4ff;color:#0969da;padding:0 4px;border-radius:4px;font-weight:600;text-decoration:none;display:inline-block;line-height:1.4}';
const MENTION_CSS_URL = 'data:text/css;charset=utf-8,' + encodeURIComponent(MENTION_CHIP_CSS);

const makeEditorConfig = (height = 160, mentionsConfig = null) => ({
  toolbar: [
    ['Bold', 'Italic', 'Underline', 'Strike'],
    ['NumberedList', 'BulletedList', 'Blockquote'],
    ['Link', 'Unlink'],
    ['Table', 'HorizontalRule'],
    ['Source'],
  ],
  height,
  removePlugins: 'elementspath',
  resize_enabled: false,
  allowedContent: true,
  contentsCss: [CK_CONTENTS_CSS, MENTION_CSS_URL, "/ckeditor-content.css"],
  bodyClass: 'document-editor',
  ...(mentionsConfig
    ? {
        extraPlugins: 'mentions,autocomplete,textmatch,textwatcher',
        mentions: [mentionsConfig],
      }
    : {}),
  // Phones get a short toolbar; desktop keeps the full grouped one.
  ...compactToolbar(),
});

// Inline an image File as a base64 data URI inside the given CKEditor instance.
const insertImageFile = (editor, file) => {
  if (!editor || !file) return;
  const reader = new FileReader();
  reader.onload = (e) => {
    editor.insertHtml(`<p><img src="${e.target.result}" alt="${file.name || 'image'}" style="max-width:100%" /></p>`);
  };
  reader.readAsDataURL(file);
};

// Wire an editor so pasting or dropping an image inlines it as base64. Without
// this, CKEditor drops pasted image files because no upload plugin is configured.
const attachImageSupport = (editor) => {
  if (!editor) return;
  const handle = (evt) => {
    const dt = evt.data && evt.data.dataTransfer;
    if (!dt || typeof dt.getFilesCount !== 'function') return;
    const count = dt.getFilesCount();
    let handled = false;
    for (let i = 0; i < count; i += 1) {
      const file = dt.getFile(i);
      if (file && typeof file.type === 'string' && file.type.indexOf('image/') === 0) {
        handled = true;
        insertImageFile(editor, file);
      }
    }
    if (handled) evt.stop();
  };
  editor.on('paste', handle);
  editor.on('drop', handle);
};

// Open a native file picker and inline the chosen image as base64 into the editor.
const pickAndInsertImage = (editor) => {
  if (!editor) return;
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.onchange = () => {
    const file = input.files && input.files[0];
    if (file) insertImageFile(editor, file);
  };
  input.click();
};

// Read the editor's live content. CKEditor 4's `change` event is best-effort and
// does not fire for undo/redo, so React draft state can lag behind what the user
// sees. Always read from the instance at submit time.
const readEditorData = (ref, fallback = '') => {
  const editor = ref && ref.current;
  if (!editor) return fallback;
  try {
    return editor.getData();
  } catch {
    return fallback;
  }
};

// Track the live instance on a ref, clear it when CKEditor destroys the editor,
// mirror undo/redo back into React state, and wire image paste support.
const bindEditor = (ref, onSync) => (e) => {
  const editor = e.editor;
  ref.current = editor;
  editor.on('destroy', () => { if (ref.current === editor) ref.current = null; });
  if (onSync) {
    editor.on('afterCommandExec', (ev) => {
      const name = ev.data && ev.data.name;
      if (name === 'undo' || name === 'redo') onSync(editor.getData());
    });
  }
  attachImageSupport(editor);
};

const isImageAttachment = (att) => {
  const name = (att?.name || att?.document?.name || '').toLowerCase();
  const ext = (att?.document?.extension || '').toLowerCase();
  const mime = att?.mime_type || att?.document?.mime_type || '';
  if (mime.startsWith('image/')) return true;
  if (/^\.?(png|jpe?g|gif|webp|bmp|svg)$/i.test(ext)) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
};

const absoluteUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
};

const attachmentUrl = (att) => {
  if (!att) return '';
  // Prefer the already-absolute URL on the nested document record.
  const direct = att.document?.document_file;
  if (direct && /^https?:\/\//i.test(direct)) return direct;
  // Otherwise fall back to relative URLs and prepend the backend origin.
  const relative = att.file_url || att.document?.document_file_url || att.document?.file_url || att.file || '';
  return absoluteUrl(relative);
};

dayjs.extend(relativeTime);

const PALETTE = [
  { bg: '#ddf4ff', fg: '#0969da', border: '#b6e3ff' },
  { bg: '#dafbe1', fg: '#1a7f37', border: '#aceebb' },
  { bg: '#fff8c5', fg: '#9a6700', border: '#f5d88c' },
  { bg: '#ffebe9', fg: '#cf222e', border: '#ffcecb' },
  { bg: '#fbefff', fg: '#8250df', border: '#ecd4ff' },
  { bg: '#fff1e5', fg: '#bc4c00', border: '#ffd8b5' },
];
const hashPalette = (str = '') => {
  let h = 0; for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};
const PRIORITY = {
  0: { label: 'Low',     pal: { bg: '#dafbe1', fg: '#1a7f37', border: '#aceebb' } },
  1: { label: 'Medium',  pal: { bg: '#ddf4ff', fg: '#0969da', border: '#b6e3ff' } },
  2: { label: 'High',    pal: { bg: '#fff1e5', fg: '#bc4c00', border: '#ffd8b5' } },
  3: { label: 'Urgent',  pal: { bg: '#ffebe9', fg: '#cf222e', border: '#ffcecb' } },
};

const isClosed = (statusName) => !!statusName && /done|closed|completed|cancel/i.test(statusName);

const OpenIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-label="Open">
    <path d="M8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
    <path fillRule="evenodd" d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0ZM1.5 8a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0Z" />
  </svg>
);
const ClosedIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-label="Closed">
    <path d="M11.28 6.78a.75.75 0 0 0-1.06-1.06L7.25 8.69 5.78 7.22a.75.75 0 0 0-1.06 1.06l2 2a.75.75 0 0 0 1.06 0l3.5-3.5Z" />
    <path fillRule="evenodd" d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0Zm-1.5 0a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z" />
  </svg>
);

const UserAvatar = ({ name, size = 24 }) => {
  const initial = (name || '?').charAt(0).toUpperCase();
  const pal = hashPalette(name || '');
  return (
    <Avatar size={size} style={{ backgroundColor: pal.fg, color: '#fff', fontSize: Math.round(size / 2.2) }}>
      {initial}
    </Avatar>
  );
};

const Label = ({ text, pal }) => {
  if (!text) return null;
  const p = pal || { bg: '#eef3f8', fg: '#1f2328', border: '#d0d7de' };
  return (
    <span className="ghi-label" style={{ background: p.bg, color: p.fg, borderColor: p.border }}>{text}</span>
  );
};

const TaskIssueDrawer = ({ visible, onClose, taskId, onSuccess, onCreated, projectId }) => {
  const currentUser = useSelector((s) => s.auth?.user);

  const [loading, setLoading] = useState(false);
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);

  const [titleEdit, setTitleEdit] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [bodyEdit, setBodyEdit] = useState(false);
  const [bodyDraft, setBodyDraft] = useState('');
  const [commentDraft, setCommentDraft] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [checklistDrafts, setChecklistDrafts] = useState({});
  const uploadRef = useRef(null);
  const composerEditorRef = useRef(null);
  const createEditorRef = useRef(null);
  const bodyEditorRef = useRef(null);
  const editingCommentEditorRef = useRef(null);
  const projectMembersRef = useRef([]);

  // Create-mode state
  const [createTitle, setCreateTitle] = useState('');
  const [createBody, setCreateBody] = useState('');
  const [createProject, setCreateProject] = useState('');
  const [createPriority, setCreatePriority] = useState(0);
  const [allProjects, setAllProjects] = useState([]);
  const [creating, setCreating] = useState(false);
  const createBodyRef = useRef('');

  // Initial load
  useEffect(() => {
    if (!visible) return;
    if (!taskId) {
      // Create mode: reset form and load projects list if no project context.
      setTask(null);
      setComments([]);
      setHistory([]);
      setCreateTitle('');
      setCreateBody('');
      createBodyRef.current = '';
      setCreatePriority(0);
      setCreateProject(projectId || '');
      if (!projectId) {
        axios.get('/project_board/projects/').then(r => {
          setAllProjects(r.data.results || r.data || []);
        }).catch(() => {});
      }
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, taskId, projectId]);

  const submitNewTask = async () => {
    const title = createTitle.trim();
    if (!title) { message.warning('Title is required'); return; }
    const proj = projectId || createProject;
    if (!proj) { message.warning('Please choose a repository (project)'); return; }
    try {
      setCreating(true);
      const res = await axios.post('/project_board/tasks/', {
        title,
        description: readEditorData(createEditorRef, createBody || createBodyRef.current || ''),
        project: proj,
        priority: createPriority,
      });
      message.success('Task created');
      const newId = res.data?.id;
      setCreateTitle('');
      setCreateBody('');
      createBodyRef.current = '';
      setCreatePriority(0);
      onSuccess && onSuccess();
      onCreated && onCreated(newId);
    } catch (e) {
      message.error('Failed to create task');
    } finally {
      setCreating(false);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const taskRes = await axios.get(`/project_board/tasks/${taskId}/`);
      const t = taskRes.data;
      setTask(t);
      setTitleDraft(t.title || '');
      setBodyDraft(t.description || '');

      const proj = t.project || projectId;
      const [commentsRes, statusesRes, typesRes, membersRes] = await Promise.all([
        axios.get(`/project_board/comments/?task=${taskId}&page_size=1000`),
        proj ? axios.get(`/project_board/statuses/?project=${proj}`) : Promise.resolve({ data: [] }),
        proj ? axios.get(`/project_board/task-types/?project=${proj}`) : Promise.resolve({ data: [] }),
        proj ? axios.get(`/project_board/members/?project=${proj}&page_size=1000`) : Promise.resolve({ data: [] }),
      ]);
      setComments((commentsRes.data.results || commentsRes.data || []).slice().sort(
        (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf()
      ));
      setStatuses(statusesRes.data.results || statusesRes.data || []);
      setTaskTypes(typesRes.data.results || typesRes.data || []);
      // Normalise to { id: user_id, user_name, employee_id } — only members of THIS project.
      const members = membersRes.data.results || membersRes.data || [];
      const normalized = members.map(m => ({
        id: m.user_id,
        user_name: m.user_name || m.user_email,
        employee_id: m.employee_id,
      })).filter(m => m.id);
      setProjectMembers(normalized);
      projectMembersRef.current = normalized;
      reloadHistory();
    } catch (e) {
      message.error('Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  // Field-level change log from django-simple-history (newest first).
  const reloadHistory = async () => {
    try {
      const res = await axios.get(`/project_board/tasks/${taskId}/history/`);
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch {
      setHistory([]);
    }
  };

  const reloadComments = async () => {
    const res = await axios.get(`/project_board/comments/?task=${taskId}&page_size=1000`);
    const list = (res.data.results || res.data || []).slice().sort(
      (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf()
    );
    setComments(list);
  };

  const patchTask = async (patch) => {
    try {
      const res = await axios.patch(`/project_board/tasks/${taskId}/`, patch);
      setTask(prev => ({ ...prev, ...res.data }));
      onSuccess && onSuccess();
      await Promise.all([reloadComments(), reloadHistory()]);
    } catch (e) {
      message.error('Failed to update');
    }
  };

  const saveTitle = async () => {
    if (!titleDraft.trim()) return;
    await patchTask({ title: titleDraft.trim() });
    setTitleEdit(false);
  };

  const saveBody = async () => {
    await patchTask({ description: readEditorData(bodyEditorRef, bodyDraft) });
    setBodyEdit(false);
  };

  const isEmptyHtml = (html) => {
    if (!html) return true;
    // Embedded media counts as content even when there is no text.
    if (/<(img|table|iframe|video|hr)\b/i.test(html)) return false;
    return !html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  };

  const postComment = async (closeAfter = false) => {
    const html = readEditorData(composerEditorRef, commentDraft);
    // Resync so the Comment button reflects what the editor actually holds.
    setCommentDraft(html);
    const hasContent = !isEmptyHtml(html);
    if (!hasContent && !closeAfter) return;
    try {
      if (hasContent) {
        await axios.post('/project_board/comments/', {
          task: taskId,
          text: html,
          comment_type: 'COMMENT',
        });
        setCommentDraft('');
        if (composerEditorRef.current) composerEditorRef.current.setData('');
      }
      if (closeAfter) {
        await toggleOpenClosed();
      }
      await reloadComments();
    } catch (e) {
      message.error('Failed to post comment');
    }
  };

  const updateComment = async (commentId) => {
    const html = readEditorData(editingCommentEditorRef, editingCommentText);
    if (isEmptyHtml(html)) return;
    try {
      await axios.patch(`/project_board/comments/${commentId}/`, { text: html });
      setEditingCommentId(null);
      setEditingCommentText('');
      await reloadComments();
    } catch (e) {
      message.error('Failed to update comment');
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await axios.delete(`/project_board/comments/${commentId}/`);
      await reloadComments();
    } catch (e) {
      message.error('Failed to delete comment');
    }
  };

  // Mentions config for the comment composer. Reads from a ref so the feed
  // always sees the latest project members without rebuilding the editor.
  const composerMentionsConfig = useMemo(() => ({
    marker: '@',
    minChars: 0,
    itemsLimit: 8,
    feed: ({ query }, callback) => {
      const q = (query || '').toLowerCase();
      const items = (projectMembersRef.current || [])
        .filter(m => !q || (m.user_name || '').toLowerCase().includes(q))
        .slice(0, 8)
        .map(m => ({
          id: m.id,
          name: m.user_name,
          userId: m.id,
        }));
      callback(items);
    },
    itemTemplate:
      '<li data-id="{id}" class="ck-mention-item">' +
      '<span class="ck-mention-avatar">{name}</span>' +
      '</li>',
    outputTemplate:
      '<a class="mention" data-user-id="{userId}" href="#" rel="nofollow">{name}</a>&nbsp;',
  }), []);

  const composerEditorConfig = useMemo(
    () => makeEditorConfig(160, composerMentionsConfig),
    [composerMentionsConfig]
  );

  const closed = isClosed(task?.status_name);

  const toggleOpenClosed = async () => {
    if (!statuses.length) { message.warning('No statuses configured for this project'); return; }
    const target = closed
      ? statuses.find(s => s.is_initial) || statuses[0]
      : statuses.find(s => s.is_completed) || statuses.find(s => /done|closed|complete/i.test(s.name)) || statuses[statuses.length - 1];
    if (target) await patchTask({ status: target.id });
  };

  const changeStatus = async (statusId) => {
    await patchTask({ status: statusId });
  };

  const changePriority = async (val) => {
    await patchTask({ priority: val });
  };

  const changeType = async (val) => {
    await patchTask({ task_type: val });
  };

  const changeDue = async (d) => {
    await patchTask({ due_date: d ? d.format('YYYY-MM-DD') : null });
  };

  const changeAssignees = async (userIds) => {
    const current = (task?.task_members || []).map(m => m.user).filter(Boolean);
    const toAdd = userIds.filter(id => !current.includes(id));
    const toRemove = (task?.task_members || []).filter(m => m.user && !userIds.includes(m.user));
    try {
      await Promise.all([
        ...toAdd.map(uid => axios.post('/project_board/task-members/', { task: taskId, user: uid })),
        ...toRemove.map(m => axios.delete(`/project_board/task-members/${m.id}/`)),
      ]);
      await loadAll();
      onSuccess && onSuccess();
    } catch (err) {
      const data = err?.response?.data;
      const reason = data?.user?.[0] || data?.error || data?.detail
        || (typeof data === 'string' ? data : null);
      message.error(reason || 'Failed to update assignees');
    }
  };

  const addChecklist = async () => {
    const name = prompt('Checklist name');
    if (!name) return;
    try {
      await axios.post('/project_board/checklists/', { task: taskId, name, items: [] });
      await loadAll();
    } catch { message.error('Failed to add checklist'); }
  };

  const toggleChecklistItem = async (cl, idx) => {
    const newItems = cl.items.map((it, i) => i === idx ? { ...it, checked: !it.checked } : it);
    try {
      await axios.patch(`/project_board/checklists/${cl.id}/`, { items: newItems });
      await loadAll();
    } catch { message.error('Failed to update checklist'); }
  };

  const addChecklistItem = async (cl) => {
    const text = (checklistDrafts[cl.id] || '').trim();
    if (!text) return;
    const newItems = [...(cl.items || []), { text, checked: false }];
    try {
      await axios.patch(`/project_board/checklists/${cl.id}/`, { items: newItems });
      setChecklistDrafts(d => ({ ...d, [cl.id]: '' }));
      await loadAll();
    } catch { message.error('Failed to add item'); }
  };

  const deleteChecklist = async (cl) => {
    try {
      await axios.delete(`/project_board/checklists/${cl.id}/`);
      await loadAll();
    } catch { message.error('Failed to delete checklist'); }
  };

  const uploadAttachment = async (file) => {
    const fd = new FormData();
    fd.append('task', taskId);
    fd.append('file', file);
    fd.append('name', file.name);
    try {
      const res = await axios.post('/project_board/attachments/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newAtt = res.data || {};
      const url = attachmentUrl(newAtt);
      const isImage = (file.type && file.type.startsWith('image/')) || isImageAttachment(newAtt);
      if (composerEditorRef.current && url) {
        const snippet = isImage
          ? `<p><img src="${url}" alt="${file.name}" style="max-width:100%" /></p>`
          : `<p><a href="${url}" target="_blank" rel="noreferrer">${file.name}</a></p>`;
        composerEditorRef.current.insertHtml(snippet);
        setCommentDraft(composerEditorRef.current.getData());
      }
      await loadAll();
      message.success('Attached');
    } catch { message.error('Failed to upload'); }
    return false;
  };

  const deleteAttachment = async (att) => {
    try {
      await axios.delete(`/project_board/attachments/${att.id}/`);
      await loadAll();
    } catch { message.error('Failed to delete'); }
  };

  const authorName = useMemo(() => {
    if (!task) return '';
    return task.created_by_name || task.created_by_user_name || task.created_by || 'user';
  }, [task]);

  const canEditComment = (c) => !!currentUser && (c.user === currentUser.id || c.created_by === currentUser.id);

  const renderTimelineItem = (c) => {
    if (c.comment_type === 'ACTIVITY') {
      return (
        <div key={c.id} className="gh-issue__event">
          <span className="ghi-event-icon">•</span>
          <span><span className="ghi-event-author">{c.user_name || 'system'}</span> {c.text} <span> · {dayjs(c.created_at).fromNow()}</span></span>
        </div>
      );
    }
    const isEditing = editingCommentId === c.id;
    return (
      <div key={c.id} className="gh-issue__card">
        <div className="gh-issue__card-head">
          <UserAvatar name={c.user_name} size={24} />
          <span className="ghi-author">{c.user_name || 'user'}</span>
          <span className="ghi-time">commented {dayjs(c.created_at).fromNow()}</span>
          {canEditComment(c) && !isEditing && (
            <div className="ghi-head-actions">
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    { key: 'edit', label: 'Edit', onClick: () => { setEditingCommentId(c.id); setEditingCommentText(c.text); } },
                    { key: 'delete', label: 'Delete', danger: true, onClick: () => deleteComment(c.id) },
                  ],
                }}
              >
                <button className="ghi-head-btn" type="button"><MoreOutlined /></button>
              </Dropdown>
            </div>
          )}
        </div>
        {isEditing ? (
          <div className="gh-issue__card-body">
            <CKEditor
              initData={editingCommentText}
              editorUrl={CK_EDITOR_URL}
              config={makeEditorConfig(160)}
              onInstanceReady={bindEditor(editingCommentEditorRef, setEditingCommentText)}
              onChange={(evt) => setEditingCommentText(evt.editor.getData())}
            />
            <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="gh-issue__btn" type="button" onClick={() => { setEditingCommentId(null); setEditingCommentText(''); }}>Cancel</button>
              <button className="gh-issue__btn is-primary" type="button" onClick={() => updateComment(c.id)}>Update comment</button>
            </div>
          </div>
        ) : (
          <div className="gh-issue__card-body" dangerouslySetInnerHTML={{ __html: c.text || '' }} />
        )}
      </div>
    );
  };

  if (!visible) return null;

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      width="min(1180px, 95vw)"
      closable={false}
      bodyStyle={{ padding: 0, background: '#fff' }}
      headerStyle={{ display: 'none' }}
      destroyOnClose
    >
      {!taskId ? (
        <div className="gh-issue gh-issue--create">
          <div className="gh-issue__header">
            <div className="gh-issue__title-row">
              <div className="gh-issue__title" style={{ fontSize: 20 }}>Create new task</div>
              <div className="gh-issue__title-btns">
                <Tooltip title="Close panel">
                  <button className="gh-issue__btn" onClick={onClose}><CloseOutlined /></button>
                </Tooltip>
              </div>
            </div>
            <div className="gh-issue__meta">Fill in the details and submit — attachments and comments become available after the task is created.</div>
          </div>

          <div className="gh-issue__body" style={{ gridTemplateColumns: '1fr' }}>
            <div className="gh-issue__main">
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--gh-text-muted)', marginBottom: 4 }}>Title</label>
                <input
                  autoFocus
                  value={createTitle}
                  onChange={(e) => setCreateTitle(e.target.value)}
                  placeholder="Title"
                  style={{ width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid var(--gh-border)', borderRadius: 6 }}
                />
              </div>

              {!projectId && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--gh-text-muted)', marginBottom: 4 }}>Repository</label>
                  <Select
                    style={{ width: '100%' }}
                    value={createProject || undefined}
                    onChange={setCreateProject}
                    placeholder="Select a project"
                    options={(allProjects || []).map(p => ({ value: p.id, label: p.project_code ? `${p.project_code} · ${p.name}` : p.name }))}
                    showSearch
                    optionFilterProp="label"
                  />
                </div>
              )}

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--gh-text-muted)', marginBottom: 4 }}>Priority</label>
                <Select
                  style={{ width: 200 }}
                  value={createPriority}
                  onChange={setCreatePriority}
                  options={[
                    { value: 0, label: 'Low' },
                    { value: 1, label: 'Medium' },
                    { value: 2, label: 'High' },
                    { value: 3, label: 'Urgent' },
                  ]}
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gh-text-muted)' }}>Description</label>
                  <button type="button" className="gh-issue__btn" onClick={() => pickAndInsertImage(createEditorRef.current)}>
                    <PaperClipOutlined /> Insert image
                  </button>
                </div>
                <CKEditor
                  initData=""
                  editorUrl={CK_EDITOR_URL}
                  config={makeEditorConfig(220)}
                  onInstanceReady={bindEditor(createEditorRef, (data) => { createBodyRef.current = data; setCreateBody(data); })}
                  onChange={(evt) => {
                    const data = evt.editor.getData();
                    createBodyRef.current = data;
                    setCreateBody(data);
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="gh-issue__btn" onClick={onClose} disabled={creating}>Cancel</button>
                <button className="gh-issue__btn is-primary" onClick={submitNewTask} disabled={creating || !createTitle.trim()}>
                  {creating ? 'Creating…' : 'Submit new task'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : loading || !task ? (
        <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
      ) : (
        <div className="gh-issue">
          {/* Header */}
          <div className="gh-issue__header">
            <div className="gh-issue__title-row">
              {titleEdit ? (
                <div className="gh-issue__title-edit">
                  <input value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} autoFocus />
                </div>
              ) : (
                <div className="gh-issue__title" onClick={() => { setTitleDraft(task.title); setTitleEdit(true); }}>
                  {task.title}
                  {task.task_code && <span className="ghi-code">#{task.task_code}</span>}
                </div>
              )}
              <div className="gh-issue__title-btns">
                {titleEdit ? (
                  <>
                    <button className="gh-issue__btn is-primary" onClick={saveTitle}>Save</button>
                    <button className="gh-issue__btn" onClick={() => setTitleEdit(false)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button className="gh-issue__btn" onClick={() => { setTitleDraft(task.title); setTitleEdit(true); }}>
                      <EditOutlined /> Edit
                    </button>
                    <Tooltip title="Close panel">
                      <button className="gh-issue__btn" onClick={onClose}><CloseOutlined /></button>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
            <div className="gh-issue__meta">
              <span className={`gh-issue__badge ${closed ? 'is-closed' : 'is-open'}`}>
                {closed ? <ClosedIcon /> : <OpenIcon />}
                {closed ? 'Closed' : 'Open'}
              </span>
              <span>
                <strong style={{ color: 'var(--gh-text)' }}>{authorName}</strong>
                {' '}opened this {task.task_type_name ? task.task_type_name.toLowerCase() : 'task'}{' '}
                {dayjs(task.created_at).fromNow()} · {comments.filter(c => c.comment_type === 'COMMENT').length} comments
              </span>
            </div>
          </div>

          {/* Body */}
          <div className="gh-issue__body">
            {/* Main column */}
            <div className="gh-issue__main">
              {/* Description card */}
              <div className="gh-issue__card">
                <div className="gh-issue__card-head">
                  <UserAvatar name={authorName} size={24} />
                  <span className="ghi-author">{authorName}</span>
                  <span className="ghi-time">commented {dayjs(task.created_at).fromNow()}</span>
                  <span className="ghi-author-badge">Author</span>
                  <div className="ghi-head-actions">
                    {!bodyEdit && (
                      <button className="ghi-head-btn" type="button" onClick={() => { setBodyDraft(task.description || ''); setBodyEdit(true); }}>
                        <EditOutlined />
                      </button>
                    )}
                  </div>
                </div>
                {bodyEdit ? (
                  <div className="gh-issue__card-body">
                    <CKEditor
                      initData={bodyDraft}
                      editorUrl={CK_EDITOR_URL}
                      config={makeEditorConfig(220)}
                      onInstanceReady={bindEditor(bodyEditorRef, setBodyDraft)}
                      onChange={(evt) => setBodyDraft(evt.editor.getData())}
                    />
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="gh-issue__btn" type="button" onClick={() => pickAndInsertImage(bodyEditorRef.current)} style={{ marginRight: 'auto' }}>
                        <PaperClipOutlined /> Insert image
                      </button>
                      <button className="gh-issue__btn" onClick={() => setBodyEdit(false)}>Cancel</button>
                      <button className="gh-issue__btn is-primary" onClick={saveBody}>Update</button>
                    </div>
                  </div>
                ) : task.description ? (
                  <div
                    className="gh-issue__card-body"
                    dangerouslySetInnerHTML={{ __html: task.description }}
                  />
                ) : (
                  <div className="gh-issue__card-body is-empty">No description provided.</div>
                )}
              </div>

              {/* Timeline: comments + activities interleaved by created_at */}
              {comments.map(renderTimelineItem)}

              {/* Composer */}
              <div className="gh-issue__composer">
                <div className="gh-issue__composer-body">
                  <CKEditor
                    initData={commentDraft}
                    editorUrl={CK_EDITOR_URL}
                    config={composerEditorConfig}
                    onInstanceReady={bindEditor(composerEditorRef, setCommentDraft)}
                    onChange={(evt) => setCommentDraft(evt.editor.getData())}
                  />
                </div>
                <div className="gh-issue__composer-attach">
                  <Upload
                    showUploadList={false}
                    multiple
                    beforeUpload={uploadAttachment}
                  >
                    <button className="gh-issue__btn" type="button">
                      <PaperClipOutlined /> Attach files
                    </button>
                  </Upload>
                  {(task.attachments || []).length > 0 && (
                    <div className="gh-issue__composer-attach-list">
                      {(task.attachments || []).map(att => {
                        const url = attachmentUrl(att);
                        const name = att.name || att.document?.name || 'attachment';
                        if (isImageAttachment(att) && url) {
                          return (
                            <a key={att.id} href={url} target="_blank" rel="noreferrer" className="gh-issue__composer-attach-thumb" title={name}>
                              <img src={url} alt={name} />
                            </a>
                          );
                        }
                        return (
                          <span key={att.id} className="gh-issue__composer-attach-item">
                            <PaperClipOutlined />
                            {url ? <a href={url} target="_blank" rel="noreferrer">{name}</a> : <span>{name}</span>}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="gh-issue__composer-actions">
                  <button
                    className="gh-issue__btn"
                    onClick={() => postComment(true)}
                    disabled={!statuses.length}
                  >
                    {closed ? <><OpenIcon size={14} /> Reopen{!isEmptyHtml(commentDraft) ? ' with comment' : ''}</> : <><ClosedIcon size={14} /> Close{!isEmptyHtml(commentDraft) ? ' with comment' : ' issue'}</>}
                  </button>
                  <button
                    className="gh-issue__btn is-primary"
                    onClick={() => postComment(false)}
                    disabled={isEmptyHtml(commentDraft)}
                  >
                    Comment
                  </button>
                </div>
              </div>

              {/* Change history (django-simple-history) */}
              <div className="ghi-history">
                <button type="button" className="ghi-history__head" onClick={() => setHistoryOpen(o => !o)}>
                  <HistoryOutlined />
                  <span>History ({history.length})</span>
                  <span className="ghi-history__toggle">{historyOpen ? 'Hide' : 'Show'}</span>
                </button>
                {historyOpen && (
                  <div className="ghi-history__list">
                    {history.length === 0 ? (
                      <div className="ghi-side-empty">No history recorded yet</div>
                    ) : history.map(h => (
                      <div key={h.history_id} className="ghi-history__item">
                        <UserAvatar name={h.user} size={20} />
                        <div className="ghi-history__content">
                          <div className="ghi-history__meta">
                            <span className="ghi-event-author">{h.user}</span>
                            {' '}{h.summary || (h.type === 'Created' ? 'created this task' : h.type === 'Deleted' ? 'deleted this task' : 'updated')}
                            <Tooltip title={dayjs(h.date).format('MMM DD, YYYY h:mm:ss A')}>
                              <span className="ghi-history__time"> · {dayjs(h.date).fromNow()}</span>
                            </Tooltip>
                          </div>
                          {(h.changes || []).map((c, i) => (
                            <div key={i} className="ghi-history__change">
                              <strong>{c.label}</strong>
                              {c.value !== undefined && c.value !== null ? (
                                <span>{c.field === 'description' ? ' ' : ': '}{c.value}</span>
                              ) : (
                                <>
                                  {': '}
                                  <span className="ghi-history__old">{c.old ?? 'empty'}</span>
                                  {' → '}
                                  <span className="ghi-history__new">{c.new ?? 'empty'}</span>
                                </>
                              )}
                            </div>
                          ))}
                          {h.reason && <div className="ghi-history__reason">{h.reason}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="gh-issue__sidebar">
              {/* Assignees */}
              <div className="ghi-side-section">
                <div className="ghi-side-title">
                  <span>Assignees</span>
                  <Dropdown
                    trigger={['click']}
                    dropdownRender={() => (
                      <div style={{ background: '#fff', border: '1px solid #d0d7de', borderRadius: 6, padding: 8, boxShadow: '0 8px 24px rgba(140,149,159,0.2)', minWidth: 220 }}>
                        <Select
                          mode="multiple"
                          style={{ width: 220 }}
                          value={(task.task_members || []).map(m => m.user).filter(Boolean)}
                          onChange={changeAssignees}
                          options={projectMembers.map(m => ({
                            value: m.id,
                            label: m.employee_id ? `${m.user_name} (${m.employee_id})` : m.user_name,
                          }))}
                          showSearch
                          optionFilterProp="label"
                          placeholder="Select assignees"
                        />
                      </div>
                    )}
                  >
                    <SettingOutlined className="ghi-gear" />
                  </Dropdown>
                </div>
                <div className="ghi-side-body">
                  {(task.task_members || []).length === 0 ? (
                    <div className="ghi-side-empty">No one assigned</div>
                  ) : (
                    (task.task_members || []).map(m => (
                      <div key={m.id} className="ghi-assignee-row">
                        <UserAvatar name={m.user_name} size={20} />
                        <span className="ghi-assignee-name">{m.user_name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Labels (priority + type) */}
              <div className="ghi-side-section">
                <div className="ghi-side-title">
                  <span>Labels</span>
                  <Dropdown
                    trigger={['click']}
                    dropdownRender={() => (
                      <div style={{ background: '#fff', border: '1px solid #d0d7de', borderRadius: 6, padding: 10, boxShadow: '0 8px 24px rgba(140,149,159,0.2)', minWidth: 220 }}>
                        <div style={{ fontSize: 11, color: '#59636e', marginBottom: 4 }}>Priority</div>
                        <Select
                          style={{ width: '100%', marginBottom: 10 }}
                          value={task.priority}
                          onChange={changePriority}
                          options={Object.entries(PRIORITY).map(([v, p]) => ({ value: Number(v), label: p.label }))}
                        />
                        <div style={{ fontSize: 11, color: '#59636e', marginBottom: 4 }}>Type</div>
                        <Select
                          style={{ width: '100%' }}
                          value={task.task_type}
                          onChange={changeType}
                          options={taskTypes.map(t => ({ value: t.id, label: t.name }))}
                          allowClear
                        />
                      </div>
                    )}
                  >
                    <SettingOutlined className="ghi-gear" />
                  </Dropdown>
                </div>
                <div className="ghi-side-body">
                  {PRIORITY[task.priority] && <Label text={PRIORITY[task.priority].label} pal={PRIORITY[task.priority].pal} />}
                  {task.task_type_name && <Label text={task.task_type_name} pal={hashPalette(task.task_type_name)} />}
                  {!PRIORITY[task.priority] && !task.task_type_name && <div className="ghi-side-empty">None yet</div>}
                </div>
              </div>

              {/* Status */}
              <div className="ghi-side-section">
                <div className="ghi-side-title"><span>Status</span></div>
                <div className="ghi-side-body">
                  <Select
                    style={{ width: '100%' }}
                    value={task.status}
                    onChange={changeStatus}
                    options={statuses.map(s => ({ value: s.id, label: s.name }))}
                    placeholder="Set status"
                  />
                </div>
              </div>

              {/* Project */}
              <div className="ghi-side-section">
                <div className="ghi-side-title"><span>Projects</span></div>
                <div className="ghi-side-body">
                  {task.project_name ? (
                    <Label text={`${task.project_code || ''} ${task.project_name}`.trim()} pal={{ bg: '#eef3f8', fg: '#1f2328', border: '#d0d7de' }} />
                  ) : <div className="ghi-side-empty">None</div>}
                </div>
              </div>

              {/* Milestone = Due date */}
              <div className="ghi-side-section">
                <div className="ghi-side-title"><span>Milestone</span></div>
                <div className="ghi-side-body">
                  <DatePicker
                    style={{ width: '100%' }}
                    value={task.due_date ? dayjs(task.due_date) : null}
                    onChange={changeDue}
                    placeholder="No due date"
                    allowClear
                  />
                </div>
              </div>

              {/* Checklists */}
              <div className="ghi-side-section">
                <div className="ghi-side-title">
                  <span>Checklists</span>
                  <Tooltip title="Add checklist"><PlusOutlined onClick={addChecklist} style={{ cursor: 'pointer' }} /></Tooltip>
                </div>
                <div className="ghi-side-body">
                  {(task.checklists || []).length === 0 && <div className="ghi-side-empty">None</div>}
                  {(task.checklists || []).map(cl => (
                    <div key={cl.id} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                        <CheckSquareOutlined /> {cl.name}
                        <Popconfirm title="Delete checklist?" onConfirm={() => deleteChecklist(cl)}>
                          <button className="ghi-head-btn" type="button" style={{ marginLeft: 'auto' }}><DeleteOutlined /></button>
                        </Popconfirm>
                      </div>
                      {(cl.items || []).map((it, i) => (
                        <label key={i} className="ghi-checklist-item">
                          <input type="checkbox" checked={!!it.checked} onChange={() => toggleChecklistItem(cl, i)} />
                          <span className={it.checked ? 'is-done' : ''}>{it.text}</span>
                        </label>
                      ))}
                      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        <input
                          placeholder="Add item"
                          value={checklistDrafts[cl.id] || ''}
                          onChange={(e) => setChecklistDrafts(d => ({ ...d, [cl.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') addChecklistItem(cl); }}
                          style={{ flex: 1, fontSize: 12, padding: '4px 8px', border: '1px solid #d0d7de', borderRadius: 4 }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Attachments */}
              <div className="ghi-side-section">
                <div className="ghi-side-title">
                  <span>Attachments</span>
                  <Upload
                    ref={uploadRef}
                    showUploadList={false}
                    beforeUpload={uploadAttachment}
                  >
                    <PaperClipOutlined style={{ cursor: 'pointer' }} />
                  </Upload>
                </div>
                <div className="ghi-side-body">
                  {(task.attachments || []).length === 0 ? (
                    <div className="ghi-side-empty">None yet — drop a file below or click the paperclip</div>
                  ) : (
                    (task.attachments || []).map(att => {
                      const url = attachmentUrl(att);
                      const name = att.name || att.document?.name || 'attachment';
                      const isImg = isImageAttachment(att);
                      return (
                        <div key={att.id} className="ghi-attachment">
                          {isImg && url ? (
                            <a href={url} target="_blank" rel="noreferrer" className="ghi-att-thumb">
                              <img src={url} alt={name} />
                            </a>
                          ) : (
                            <PaperClipOutlined />
                          )}
                          {url
                            ? <a href={url} target="_blank" rel="noreferrer">{name}</a>
                            : <span>{name}</span>}
                          {url && (
                            <a href={url} download title="Download" style={{ marginLeft: 'auto', color: 'var(--gh-text-muted)' }}>
                              <DownloadOutlined />
                            </a>
                          )}
                          <Popconfirm title="Delete attachment?" onConfirm={() => deleteAttachment(att)}>
                            <button className="ghi-att-del" type="button"><DeleteOutlined /></button>
                          </Popconfirm>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Dates */}
              <div className="ghi-side-section">
                <div className="ghi-side-title"><span>Dates</span></div>
                <div className="ghi-side-body" style={{ fontSize: 12, color: 'var(--gh-text-muted)' }}>
                  <div>Start (est): <DatePicker size="small" value={task.estimated_start_date ? dayjs(task.estimated_start_date) : null} onChange={(d) => patchTask({ estimated_start_date: d ? d.format('YYYY-MM-DD') : null })} /></div>
                  <div style={{ marginTop: 6 }}>End (est): <DatePicker size="small" value={task.estimated_end_date ? dayjs(task.estimated_end_date) : null} onChange={(d) => patchTask({ estimated_end_date: d ? d.format('YYYY-MM-DD') : null })} /></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default TaskIssueDrawer;
